import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'

import { filteredSteps } from '#app/features/form/wizard/utils/filteredSteps.ts'
import { getCombinedSteps } from '#app/features/form/wizard/utils/getCombinedSteps.ts'
import { WizardForm } from '#app/features/form/wizard/wizardForm/index.tsx'
import { useGlobalIpipNeoFormStore } from '#app/routes/test+/big-five-personality.tsx'
import { type Steps } from '#types/form/wizard.ts'

import { GetSteps as IpipGetSteps } from './getSteps.tsx'

export const IpipNeoForm: React.FC<{ data: any }> = ({ data }) => {
	const { t } = useTranslation(['intakeForm', 'ipipForm'])

	const currentLanguage = useGlobalIpipNeoFormStore(state => state.languageId)
	const includedControllerQuestions = useGlobalIpipNeoFormStore(
		useShallow(state => state.includedControllerQuestions),
	)
	const questionTree = useGlobalIpipNeoFormStore(
		useShallow(state => state.questionTree),
	)
	const answeredQuestionsStack = useGlobalIpipNeoFormStore(
		useShallow(state => state.answeredQuestionsStack),
	)
	const currentStep = useGlobalIpipNeoFormStore(state => state.currentStep)
	const currentQuestionId = useGlobalIpipNeoFormStore(
		state => state.currentQuestionId,
	)
	const currentQuestionTreeId = useGlobalIpipNeoFormStore(
		state => state.currentQuestionTreeId,
	)

	const editableParents = useGlobalIpipNeoFormStore(
		state => state.editableParents,
	)

	const combinedGetSteps = getCombinedSteps({
		steps: [
			{ getSteps: IpipGetSteps, includeContext: true, needsController: false },
		],
		contextParams: {
			currentQuestionId,
			currentQuestionTreeId,
			editableParents: editableParents || [],
			questionTree,
			answeredQuestionsStack,
		},
		t,
	})

	const allCurrentSteps = filteredSteps({
		GetSteps: combinedGetSteps,
		includedControllerQuestions: includedControllerQuestions as any,
	})()

	const currentStepProps = allCurrentSteps[currentStep]
	const isLastStep = allCurrentSteps.length === currentStep - 1

	const formText = {
		title: t('formSetup.title', { ns: 'ipipForm' }),
		subtitle: t('formSetup.subtitle', { ns: 'ipipForm' }),
		finishedText: t('formSetup.finishedText', { ns: 'ipipForm' }),
		finishedSubtitle: t('formSetup.finishedSubtitle', { ns: 'ipipForm' }),
		finishedButtonText: t('formSetup.finishedButtonText', { ns: 'ipipForm' }),
	}

	const currentStepPropsId = currentStepProps?.id as string
	return (
		<WizardForm
			actionRoute={`/${currentLanguage}/intake`}
			translationStrings={['introductionForm']}
			data={data}
			useGlobalFormStore={useGlobalIpipNeoFormStore}
			getSteps={filteredSteps({
				GetSteps: combinedGetSteps,
				includedControllerQuestions: includedControllerQuestions || [],
			})}
			currentSteps={
				filteredSteps({
					GetSteps: combinedGetSteps,
					includedControllerQuestions: includedControllerQuestions || [],
				})()[currentStep] as Steps<any>
			}
			currentStepProps={{
				id: currentStepPropsId,
				fieldKey: currentStepProps?.fieldKey ?? '',
				isInJsonTree: currentStepProps?.isInJsonTree,
				questionText: currentStepProps?.questionText,
				validator: currentStepProps?.validator,
				component: currentStepProps?.component,
				formHasBeenCompleted:
					data?.data?.globalState?.formHasBeenCompleted ?? false,
				hasBackendCall: currentStepProps?.hasBackendCall ?? false,
				backendAction: currentStepProps?.backendAction,
				isLastStep,
			}}
			formText={formText}
		/>
	)
}
